import isDefined from 'isDefined';
import metricTypeCheckers from 'types/metricTypeCheckers';
import { getSeriesDataKey } from 'components/V5Gadget/seriesDataKey';

import { toTermName } from './rangeTerms';

export interface ScatterPoint {
  // The dimension member the point stands for
  name: string;
  x: number;
  y: number;
  // A bubble's size: 1 for every point without a size metric
  z: number;
}

export interface ScatterPoints {
  points: ScatterPoint[];
  x: V5ChartData[string];
  y: V5ChartData[string];
  size?: V5ChartData[string];
  // The metrics the context menu opens for (compound metrics' inputs)
  metricIds: string[];
}

const collectMetricIds = (metric: Metrics.Metric): string[] =>
  metricTypeCheckers.isCompoundMetric(metric) ? metric.metricIds : [metric.id];

// A scatter or bubble chart's points: series[0] is the x metric, series[1]
// the y metric and, on a bubble chart, series[2] the size. Each dimension
// member becomes one point, joining the metrics' responses on the group
// field. Members missing a value are left out. Undefined without an x and y metric
// and a field to plot by.
const toScatterPoints = ({
  data,
  chartDef,
  groupField,
}: {
  data: V5ChartData;
  chartDef: V5ChartDefinition;
  groupField: string;
}): ScatterPoints | undefined => {
  const [xDef, yDef, bubbleSizeDef] = chartDef.series;
  // Only a bubble chart sizes its points
  const sizeDef = chartDef.gadgetType === 'bubble' ? bubbleSizeDef : undefined;
  const x = xDef ? data[getSeriesDataKey(xDef)] : undefined;
  const y = yDef ? data[getSeriesDataKey(yDef)] : undefined;
  const size = sizeDef ? data[getSeriesDataKey(sizeDef)] : undefined;
  if (!x || !y || !groupField) {
    return undefined;
  }

  // Range buckets come back under their own key (see toTermName)
  const memberOf = (row: MetricResponse): string =>
    chartDef.dimensionA && chartDef.dimensionA.rangeInput
      ? toTermName({ chartDef, row })
      : String(row[groupField]);
  const byMember = (entry: V5ChartData[string], metricId: string) =>
    new Map(
      entry.response.map((row) => [memberOf(row), row[metricId] as number]),
    );
  const yByMember = byMember(y, yDef.metricId);
  const sizeByMember =
    size && sizeDef ? byMember(size, sizeDef.metricId) : undefined;

  const points = x.response
    .map((row): ScatterPoint | undefined => {
      const member = memberOf(row);
      const xValue = Number(row[xDef.metricId]);
      const yValue = Number(yByMember.get(member));
      // Uniform size until a third (size) metric is configured
      const zValue = sizeByMember ? Number(sizeByMember.get(member)) : 1;
      if (isNaN(xValue) || isNaN(yValue) || isNaN(zValue)) {
        return undefined;
      }
      return { name: member, x: xValue, y: yValue, z: zValue };
    })
    .filter(isDefined);

  return {
    points,
    x,
    y,
    size,
    metricIds: [
      ...collectMetricIds(x.metric),
      ...collectMetricIds(y.metric),
      ...(size ? collectMetricIds(size.metric) : []),
    ],
  };
};

export default toScatterPoints;
